import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import { Play, Pause, Scissors } from 'lucide-react';

const formatTime = (s) => {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${m}:${rem.toString().padStart(2, '0')}`;
};

/**
 * Waveform + draggable 30s region for picking the preview section from a full audio file.
 *
 * Props:
 *   file: File (the full audio uploaded by the admin — read via ObjectURL, never uploaded here)
 *   duration: number (seconds — passed in, so we don't wait for wavesurfer to finish decoding)
 *   defaultStart: number (start seconds; used when re-rendering after a state change)
 *   onChange({ start, end }): called whenever the admin drags the region
 *
 * Behavior:
 *   - Region is exactly `regionSeconds` wide (default 30s). Admin can drag it left/right
 *     along the waveform to pick where the preview begins.
 *   - Play/Pause button plays ONLY the highlighted region and loops back to region start.
 *   - Waveform theme matches the store (cyan on dark).
 */
const WaveformTrimmer = ({
  file,
  defaultStart = 0,
  regionSeconds = 30,
  onChange,
  testIdPrefix = 'trimmer',
}) => {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [region, setRegion] = useState({ start: defaultStart, end: defaultStart + regionSeconds });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file || !containerRef.current) return;
    let cancelled = false;
    const url = URL.createObjectURL(file);
    const regions = RegionsPlugin.create();

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#3F3F46',
      progressColor: '#00F0FF',
      cursorColor: '#FFFFFF',
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      height: 72,
      normalize: true,
      interact: true,
      plugins: [regions],
    });
    wsRef.current = ws;

    // wavesurfer v7 .load() returns a Promise; when StrictMode remounts, the abort
    // from destroy() rejects that promise. Swallow AbortError so React's dev overlay
    // stops flagging it (production builds are unaffected).
    const loadPromise = ws.load(url);
    if (loadPromise && typeof loadPromise.catch === 'function') {
      loadPromise.catch((e) => {
        if (e && e.name !== 'AbortError') {
          console.warn('WaveSurfer load failed:', e);
        }
      });
    }

    ws.on('ready', () => {
      if (cancelled) return;
      const totalDuration = ws.getDuration();
      const start = Math.min(defaultStart, Math.max(0, totalDuration - regionSeconds));
      const end = Math.min(start + regionSeconds, totalDuration);
      const r = regions.addRegion({
        start,
        end,
        color: 'rgba(0, 240, 255, 0.20)',
        drag: true,
        resize: false, // fixed 30s window
        content: `${regionSeconds}s preview`,
      });
      regionRef.current = r;
      setRegion({ start, end });
      onChange?.({ start, end });
      setReady(true);

      // Loop playback inside the region
      r.on('out', () => {
        if (wsRef.current && !wsRef.current.isPlaying()) return;
        wsRef.current.setTime(r.start);
      });
      r.on('update', () => {
        const newRange = { start: r.start, end: r.end };
        setRegion(newRange);
        onChange?.(newRange);
      });
    });

    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => setIsPlaying(false));
    ws.on('error', (e) => {
      if (!cancelled) setError(String(e || 'Waveform failed to load'));
    });
    // Keep playhead pinned to the region while playing
    ws.on('timeupdate', (t) => {
      const r = regionRef.current;
      if (!r || !wsRef.current) return;
      if (wsRef.current.isPlaying() && t >= r.end) {
        wsRef.current.setTime(r.start);
      }
    });

    return () => {
      cancelled = true;
      try { ws.destroy(); } catch {}
      URL.revokeObjectURL(url);
    };
    // We deliberately only run this when the FILE identity changes; region updates flow
    // through onChange without re-creating wavesurfer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const toggle = () => {
    const ws = wsRef.current;
    const r = regionRef.current;
    if (!ws || !r) return;
    if (ws.isPlaying()) {
      ws.pause();
    } else {
      ws.setTime(r.start);
      ws.play();
    }
  };

  if (error) {
    return (
      <div className="text-xs text-red-400 p-3 bg-[#0F0F11] border border-[#27272A] rounded">
        Couldn't render the waveform. You can still set a preview manually below. ({error})
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-[#A1A1AA]">
          <Scissors size={12} className="text-[#00F0FF]" />
          <span>Drag the highlighted section to choose the 30-second preview</span>
        </div>
        <div className="font-mono text-[#00F0FF]" data-testid={`${testIdPrefix}-range`}>
          {formatTime(region.start)} → {formatTime(region.end)}
        </div>
      </div>
      <div
        className="bg-[#0F0F11] border border-[#27272A] rounded-md overflow-hidden"
      >
        <div ref={containerRef} className="w-full" />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          disabled={!ready}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition disabled:opacity-40"
          data-testid={`${testIdPrefix}-play-btn`}
        >
          {isPlaying ? <Pause size={12} /> : <Play size={12} />}
          {isPlaying ? 'Pause section' : 'Play section'}
        </button>
        {!ready && <span className="text-xs text-[#71717A]">Decoding waveform…</span>}
      </div>
    </div>
  );
};

export default WaveformTrimmer;
